if (!customElements.get('custom-video-tag')) {
  customElements.define(
    'custom-video-tag',
    class CustomVideoTag extends HTMLElement {
      constructor() {
        super();
        this.badge = this.querySelector('.f-badge--video');
      }

      connectedCallback() {
        if (this.badge) {
          this.badge.addEventListener('click', this.handleClick.bind(this));
          this.badge.addEventListener('keydown', this.handleKeyDown.bind(this));
          // Make it focusable for keyboard navigation
          this.badge.setAttribute('role', 'button');
          this.badge.setAttribute('tabindex', '0');
        }
      }

      disconnectedCallback() {
        if (this.badge) {
          this.badge.removeEventListener('click', this.handleClick.bind(this));
          this.badge.removeEventListener('keydown', this.handleKeyDown.bind(this));
        }
      }

      handleKeyDown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.handleClick(event);
        }
      }

      handleClick(event) {
        event.preventDefault();
        this.scrollToVideo();
      }

      isElementTopInView(element) {
        const rect = element.getBoundingClientRect();
        // Check if the top part of the element is visible in viewport
        return rect.top >= 0 && rect.top < window.innerHeight;
      }

      scrollToVideo() {
        // Find the media gallery component
        const mediaGallery = this.closest('product-info')?.querySelector('media-gallery');
        
        if (!mediaGallery) {
          console.warn('custom-video-tag: Media gallery not found');
          return;
        }

        // Find the first video media item
        const videoMediaItem = mediaGallery.querySelector(
          '.product__media-item[data-media-type="video"], .product__media-item[data-media-type="external_video"]'
        );

        if (!videoMediaItem) {
          console.warn('custom-video-tag: Video media item not found');
          return;
        }

        const mediaIndex = parseInt(videoMediaItem.dataset.mediaIndex, 10);

        if (isNaN(mediaIndex)) {
          console.warn('custom-video-tag: Invalid media index');
          return;
        }

        // Check if the top of the gallery is in view, and scroll only if not
        const needsScroll = !this.isElementTopInView(mediaGallery);
        
        if (needsScroll) {
          // Calculate scroll position with fixed offset from top
          const rect = mediaGallery.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const elementTop = rect.top + scrollTop;
          const offsetFromTop = 80;
          const scrollOffset = elementTop - offsetFromTop;
          
          window.scrollTo({
            top: Math.max(0, scrollOffset), // Ensure we don't scroll to negative position
            behavior: 'smooth'
          });
        }

        // Navigate to the video slide (with delay if we scrolled)
        const delay = needsScroll ? 100 : 0;
        setTimeout(() => {
          // Check if the gallery has a slider instance
          if (mediaGallery.sliderInstance && typeof mediaGallery.sliderInstance.slider === 'object') {
            // Use the slider's slideToLoop method (position is 0-based from forloop.index0)
            mediaGallery.sliderInstance.slider.slideToLoop(mediaIndex, 500);
          } else {
            // Fallback: scroll to the video element directly
            videoMediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, delay);
      }
    }
  );
}

/**
 * EU legal-guarantee notice modal.
 * Self-contained so the snippet can be reused outside the product page.
 */
if (!customElements.get('legal-guarantee-notice')) {
  class LegalGuaranteeNotice extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onModalClick = this.onModalClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      this.trigger = this.querySelector('[data-legal-guarantee-notice-open]');
      this.modal = this.querySelector('.legal-guarantee-notice__modal');
      if (!this.trigger || !this.modal) return;

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.modal.addEventListener('click', this.onModalClick);
    }

    disconnectedCallback() {
      this.close();
      if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
      if (this.modal) this.modal.removeEventListener('click', this.onModalClick);
    }

    onTriggerClick(event) {
      event.preventDefault();
      this.open();
    }

    onModalClick(event) {
      if (event.target.closest('[data-legal-guarantee-notice-close]')) {
        this.close();
      }
    }

    onKeyDown(event) {
      if (event.key === 'Escape') this.close();
    }

    open() {
      if (!this.modal || this.isOpen) return;

      this.modalParent = this.modal.parentNode;
      this.modalNext = this.modal.nextSibling;
      document.body.appendChild(this.modal);
      this.modal.classList.add('is-open');
      this.modal.removeAttribute('hidden');
      document.body.classList.add('legal-guarantee-notice-open');
      document.addEventListener('keydown', this.onKeyDown);
      this.isOpen = true;

      const closeButton = this.modal.querySelector('.legal-guarantee-notice__close');
      if (closeButton) closeButton.focus();
    }

    close() {
      if (!this.modal || !this.isOpen) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('hidden', '');
      document.body.classList.remove('legal-guarantee-notice-open');
      document.removeEventListener('keydown', this.onKeyDown);

      if (this.modalParent) {
        if (this.modalNext && this.modalNext.parentNode === this.modalParent) {
          this.modalParent.insertBefore(this.modal, this.modalNext);
        } else {
          this.modalParent.appendChild(this.modal);
        }
        this.modalParent = null;
        this.modalNext = null;
      }

      this.isOpen = false;
      if (this.trigger) this.trigger.focus();
    }
  }

  customElements.define('legal-guarantee-notice', LegalGuaranteeNotice);
}
